import { getDefaultSortOrder, List, useTable } from '@refinedev/antd';
import type { BaseRecord, CrudFilter } from '@refinedev/core';
import { Input, Select, Space, Table } from 'antd';
import { renderValue } from '../render';
import type { ResourceConfig } from '../resources';

/** A resource's list: configured columns, server-side sorting, filters and search, state in the URL. */
export function ResourceList( { config }: { config: ResourceConfig } ) {
	const { tableProps, sorters, filters, setFilters } = useTable<BaseRecord>( {
		resource: config.name,
		syncWithLocation: true,
		pagination: { pageSize: 50 },
		...( config.defaultSort ? { sorters: { initial: [ config.defaultSort ] } } : {} ),
	} );
	const current = ( field: string ) => ( filters.find( ( f ) => 'field' in f && f.field === field ) as ( CrudFilter & { value?: unknown } ) | undefined )?.value;
	const set = ( field: string, value: unknown ) => setFilters( [ { field, operator: 'eq', value: value === '' ? undefined : value } ], 'merge' );

	return (
		<List title={ config.label }>
			<Space wrap style={ { marginBottom: 16 } }>
				{ config.search ? (
					<Input.Search allowClear placeholder="Search" defaultValue={ ( current( 'q' ) as string ) ?? '' } onSearch={ ( value ) => set( 'q', value ) } style={ { width: 260 } } />
				) : null }
				{ ( config.filters ?? [] ).map( ( filter ) => (
					<Select
						key={ filter.key }
						allowClear
						placeholder={ filter.title }
						value={ current( filter.key ) as string | undefined }
						onChange={ ( value ) => set( filter.key, value ) }
						options={ filter.options.map( ( value ) => ( { value, label: value } ) ) }
						style={ { minWidth: 160 } }
					/>
				) ) }
			</Space>
			<Table { ...tableProps } rowKey="id" size="small" scroll={ { x: true } }>
				{ config.columns.map( ( column ) => (
					<Table.Column<BaseRecord>
						key={ column.key }
						dataIndex={ column.key }
						title={ column.title ?? column.key.replace( /_/g, ' ' ) }
						sorter={ column.sort ? { multiple: 1 } : false }
						defaultSortOrder={ column.sort ? getDefaultSortOrder( column.key, sorters ) : null }
						render={ ( value ) => renderValue( column.key, value, column.link ) }
					/>
				) ) }
			</Table>
		</List>
	);
}
